import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import actor from "../../assets/actor.png";
import actress from "../../assets/actress.png";
import dailyNews from "../../assets/dailyNews.png";
import castingCall from "../../assets/castingCall.png";
import image1 from "../../assets/banner1.png";
import image2 from "../../assets/banner32.png";
import image3 from "../../assets/banner3.png";

import "./index.css";

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // API Configuration
  const BASE_URL = 'https://www.whysocial.in/clap-kartel/public';
  const IMAGE_BASE_URL = 'https://www.whysocial.in/clap-kartel/public/uploads';
  const BANNER_IMAGE_BASE_URL = 'https://www.whysocial.in/clap-kartel/public/uploads/banners';
  const CRAFT_IMAGE_BASE_URL = 'https://whysocial.in/clap-kartel/public/uploads/cat-list';

  // State for API data
  const [podcastData, setPodcastData] = useState([]);
  const [bannerData, setBannerData] = useState([]);
  const [craftCategories, setCraftCategories] = useState([]);
  const [otherSections, setOtherSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for hero carousel
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPlaying, setHeroPlaying] = useState(false);

  // State for category filter pills
  const [activeCategory, setActiveCategory] = useState(null);

  // State for craft tabs
  const [activeTab, setActiveTab] = useState('24crafts');

  // State for craft modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCraft, setSelectedCraft] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [subCategoryError, setSubCategoryError] = useState(null);

  // State for craft rentals subcategories (shown in Other Crafts > Rentals)
  const [craftRentalsSubcategories, setCraftRentalsSubcategories] = useState([]);

  // State for other section modal
  const [isOtherModalOpen, setIsOtherModalOpen] = useState(false);
  const [selectedOtherSection, setSelectedOtherSection] = useState(null);
  const [otherCategories, setOtherCategories] = useState([]);
  const [loadingOtherCategories, setLoadingOtherCategories] = useState(false);
  const [otherCategoryError, setOtherCategoryError] = useState(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Helper function to get authorization headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Helper function to convert text to title case
  const toTitleCase = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to construct banner image URL
  const getBannerImageUrl = (imageName) => {
    if (!imageName) return '';
    if (imageName.startsWith('http')) return imageName;
    return `${BANNER_IMAGE_BASE_URL}/${imageName}`;
  };

  // Helper function to construct craft category image URL
  const getCraftImageUrl = (imageName) => {
    if (!imageName) return null;
    if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
      return imageName;
    }
    return `${CRAFT_IMAGE_BASE_URL}/${imageName}`;
  };

  // Helper function to construct other section image URL
  const getOtherSectionImageUrl = (imageName) => {
    if (!imageName) return '';
    if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
      return imageName;
    }
    return `${CRAFT_IMAGE_BASE_URL}/${imageName}`;
  };

  // Helper function to build rental sub image URL from specific image mapping
  const getRentalSubImage = (subName, apiImage) => {
    const rentalImagesMap = {
      'drone': 'drones.png',
      'nagara': 'Editing Studios.png',
      'rentals': 'rentals.png',
      'cameras': 'camers.png',
      'caravan': 'caravan 1.png',
      'costumes': 'coustmes.png',
      'locations': 'locationn.png',
      'ac anaconda': 'AC Anaconda.png',
      'rain effect': 'rain effect.png',
      'outdoor fans': 'outdoor fans.png',
      'gopro cameras': 'Gopro Camera.png',
      'unit vehicles': 'unit vechiles.png',
      'editing studios': 'Editing Studio.png',
      'grading studios': 'Grading Studio.png',
      'dubbing studios': 'Dubbing Studio.png',
      'podcast studios': 'Podcast Studio.png',
      'choreography studios': 'Choreography Studio.png',
      'tent house equipment': 'Tent House Equipment.png',
      'greenmat / bluemat clothes': 'Green Mat : Blue Mat Clothes.png',
      'properties / set suppliers': 'Properties : Set Suppliers.png',
      'premium vehicles for imported': 'Premium Vehicles for Imported.png',
    };

    if (subName) {
      const normalizedName = subName.trim().toLowerCase();
      if (rentalImagesMap[normalizedName]) {
        return `https://whysocial.in/clap-kartel/public/uploads/cat-list/${rentalImagesMap[normalizedName]}`;
      }
    }

    // Fallback to what was provided by API
    if (!apiImage) return '';
    if (apiImage.startsWith('http://') || apiImage.startsWith('https://')) {
      return apiImage;
    }
    return `${CRAFT_IMAGE_BASE_URL}/${apiImage}`;
  };

  // Helper function to convert YouTube watch URL to embed URL
  const convertToEmbedUrl = (url, autoplay = false) => {
    if (!url) return '';
    if (url.includes('youtube.com/embed/')) {
      // Strip existing params and add our own
      const baseEmbed = url.split('?')[0];
      return `${baseEmbed}?mute=1&controls=1&modestbranding=1&rel=0${autoplay ? '&autoplay=1' : ''}`;
    }
    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (videoIdMatch && videoIdMatch[1]) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}?mute=1&controls=1&modestbranding=1&rel=0${autoplay ? '&autoplay=1' : ''}`;
    }
    return url;
  };

  // Build hero video slides from the API response
  const getHeroVideos = useCallback(() => {
    if (podcastData && podcastData.length > 0) {
      // Map all video URLs from the response
      return podcastData.map(podcast => podcast.video_url).filter(Boolean);
    }
    const defaultUrl = "https://www.youtube.com/watch?v=qtUIxjJDZK0";
    // Default fallback videos
    return [defaultUrl, defaultUrl, defaultUrl, defaultUrl, defaultUrl];
  }, [podcastData]);

  const heroVideos = getHeroVideos();
  const totalHero = heroVideos.length;

  const heroNext = () => setHeroIndex((prev) => (prev + 1) % totalHero);
  const heroPrev = () => setHeroIndex((prev) => (prev - 1 + totalHero) % totalHero);

  // Function to fetch subcategories when a craft is clicked
  const fetchSubCategories = async (catId) => {
    try {
      setLoadingSubCategories(true);
      setSubCategoryError(null);
      setSubCategories([]);
      const response = await fetch(`${BASE_URL}/api/allSubCategoryList/${catId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`Failed to fetch subcategories: ${response.status}`);
      const result = await response.json();
      if (result?.subcategorylist && Array.isArray(result.subcategorylist) && result.subcategorylist.length > 0) {
        setSubCategories(result.subcategorylist);
      } else if (result?.data && Array.isArray(result.data) && result.data.length > 0) {
        setSubCategories(result.data);
      } else if (Array.isArray(result) && result.length > 0) {
        setSubCategories(result);
      } else {
        setSubCategories([]);
      }
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setSubCategoryError(err.message);
      setSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  // Function to fetch other categories when other section is clicked
  const fetchOtherCategories = async (sectionId) => {
    try {
      setLoadingOtherCategories(true);
      setOtherCategoryError(null);
      setOtherCategories([]);
      setHasAttemptedFetch(false);
      const response = await fetch(`${BASE_URL}/api/get-other-categories/${sectionId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`Failed to fetch other categories: ${response.status}`);
      const result = await response.json();
      let foundCategories = [];
      if (result?.crafts241 && Array.isArray(result.crafts241)) {
        foundCategories = result.crafts241;
      } else if (result?.data && Array.isArray(result.data)) {
        foundCategories = result.data;
      } else if (result?.categories && Array.isArray(result.categories)) {
        foundCategories = result.categories;
      } else if (Array.isArray(result)) {
        foundCategories = result;
      }
      setOtherCategories(foundCategories);
      setHasAttemptedFetch(true);
    } catch (err) {
      console.error('Error fetching other categories:', err);
      setOtherCategoryError(err.message);
      setOtherCategories([]);
      setHasAttemptedFetch(true);
    } finally {
      setLoadingOtherCategories(false);
    }
  };

  // Handle craft item click
  const handleCraftClick = (craft) => {
    navigate('/craft-detail', {
      state: {
        categoryId: craft.id,
        categoryName: craft.cat_name,
        categoryImage: craft.cat_image
      }
    });
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setLoadingSubCategories(false);
    setSubCategoryError(null);
    setTimeout(() => {
      setSelectedCraft(null);
      setSubCategories([]);
    }, 300);
  };

  // Handle other section item click — same logic as /other-section page
  // If data_exists is false → go directly to /other-section-content
  // Otherwise → go to /other-section-detail for sub-category drill-down
  const handleOtherSectionClick = (section) => {
    if (section.is_craft) {
      navigate('/craft-detail', {
        state: {
          categoryId: section.id,
          categoryName: section.cat_name,
          categoryImage: section.cat_image || section.sub_picture
        }
      });
      return;
    }

    if (section.data_exists === false) {
      // No sub-detail page; navigate straight to content using section.other_id
      navigate('/other-section-content', {
        state: {
          otherId: section.other_id,
          categoryName: section.sub_cat_name
        }
      });
    } else {
      navigate('/other-section-detail', {
        state: {
          sectionId: section.id,
          sectionName: section.sub_cat_name,
          sectionImage: section.sub_picture || section.cat_image
        }
      });
    }
  };

  // Handle other section modal close
  const handleCloseOtherModal = () => {
    setIsOtherModalOpen(false);
    setLoadingOtherCategories(false);
    setOtherCategoryError(null);
    setHasAttemptedFetch(false);
    setTimeout(() => {
      setSelectedOtherSection(null);
      setOtherCategories([]);
    }, 300);
  };

  // Auto-scroll references for the promotional banners
  const promoBannerRefTop = useRef(null);
  const promoBannerRefBottom = useRef(null);
  // Ref for the Rentals section in Other Crafts
  const rentalsRef = useRef(null);

  // Auto-scroll effect for promotional banners (every 3 seconds)
  useEffect(() => {
    const scrollInterval = setInterval(() => {
      const scrollBanner = (ref) => {
        if (ref.current) {
          const { scrollLeft, scrollWidth, clientWidth } = ref.current;
          // Exact dimensions of the banner card + gap is 567.856px + 14px = ~581.856px.
          // Let's scroll by the card width roughly.
          const scrollAmount = 582;

          if (scrollLeft + clientWidth >= scrollWidth - 10) {
            // Reached the end, scroll back out to start smoothly or instantly
            ref.current.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            // Scroll right by exactly one banner width
            ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          }
        }
      };

      scrollBanner(promoBannerRefTop);
      scrollBanner(promoBannerRefBottom);
    }, 3000);

    return () => clearInterval(scrollInterval);
  }, []);

  // Effect to handle navigation state with scroll instructions
  useEffect(() => {
    if (location.state && location.state.scrollToRentals) {
      // First, set the active category and switch tab
      setActiveCategory('Rentals');
      setActiveTab('other');

      // We only scroll if the page data has finished loading!
      if (!loading) {
        setTimeout(() => {
          if (rentalsRef.current) {
            rentalsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          // Clear the state only AFTER we scroll, so it doesn't fire again
          window.history.replaceState({}, document.title);
        }, 150); // Small delay to let the DOM paint the tab switch
      }
    }
  }, [location.state, setActiveCategory, setActiveTab, loading]);

  // Fetch API data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch podcast data
        const podcastResponse = await fetch(`${BASE_URL}/getpodcast`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!podcastResponse.ok) throw new Error(`Failed to fetch podcast: ${podcastResponse.status}`);
        const podcastResult = await podcastResponse.json();
        if (podcastResult?.result && podcastResult.result.length > 0) {
          setPodcastData(podcastResult.result);
        }

        // Fetch banners
        const bannerResponse = await fetch(`${BASE_URL}/dash/getbanners/1`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!bannerResponse.ok) throw new Error(`Failed to fetch banners: ${bannerResponse.status}`);
        const bannerResult = await bannerResponse.json();
        if (bannerResult?.banners && bannerResult.banners.length > 0) {
          setBannerData(bannerResult.banners);
        }

        // Fetch category list for crafts section
        const categoryResponse = await fetch(`${BASE_URL}/api/allCategoryList`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!categoryResponse.ok) throw new Error(`Failed to fetch categories: ${categoryResponse.status}`);
        const categoryResult = await categoryResponse.json();
        if (categoryResult?.categoryList && categoryResult.categoryList.length > 0) {
          const filteredCategories = categoryResult.categoryList.filter(cat => String(cat.id) !== '22');
          setCraftCategories(filteredCategories);
        }

        // Fetch other sections/formats
        const otherSectionsResponse = await fetch(`${BASE_URL}/api/get-other-formats`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!otherSectionsResponse.ok) throw new Error(`Failed to fetch other sections: ${otherSectionsResponse.status}`);
        const otherSectionsResult = await otherSectionsResponse.json();

        if (otherSectionsResult?.subcategories) {
          let subcategories = otherSectionsResult.subcategories;
          // Remove old Rentals group from other-formats so we show craft rentals instead
          delete subcategories['Rentals'];
          setOtherSections(subcategories);
        }

        // Fetch subcategories of Craft Rentals (id 22) to display in Other Crafts > Rentals
        const rentalsSubResponse = await fetch(`${BASE_URL}/api/allSubCategoryList/22`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (rentalsSubResponse.ok) {
          const rentalsSubResult = await rentalsSubResponse.json();
          let subs = [];
          if (rentalsSubResult?.subcategorylist && Array.isArray(rentalsSubResult.subcategorylist)) {
            subs = rentalsSubResult.subcategorylist;
          } else if (rentalsSubResult?.data && Array.isArray(rentalsSubResult.data)) {
            subs = rentalsSubResult.data;
          } else if (Array.isArray(rentalsSubResult)) {
            subs = rentalsSubResult;
          }
          setCraftRentalsSubcategories(subs);
        }

      } catch (err) {
        console.error('Error fetching homepage data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fallback images for banners
  const fallbackBanners = [image1, image2, image3, image1, image2, image3];

  const categoryList = [
    { name: "Cast & Crew", image: actor, path: "/cast-crew" },
    { name: "Lessons", image: dailyNews, path: null },
    { name: "Rentals", image: castingCall, path: null },
    { name: "Directory", image: actress, path: "/directory" },
  ];

  // Get the index for left/right preview in hero carousel
  const getHeroSlideIndex = (offset) => {
    return (heroIndex + offset + totalHero) % totalHero;
  };

  // Render modal body content for Other Sections
  const renderOtherModalContent = () => {
    if (loadingOtherCategories) {
      return (
        <div className="modal-loading">
          <motion.div
            className="modal-loader"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p>Loading categories...</p>
        </div>
      );
    }
    if (otherCategoryError) {
      return (
        <div className="modal-error">
          <div className="no-profiles-icon">⚠️</div>
          <h3 className="no-profiles-title">Error Loading Profiles</h3>
          <p className="no-profiles-subtitle">{otherCategoryError}</p>
        </div>
      );
    }
    if (hasAttemptedFetch && (!otherCategories || !Array.isArray(otherCategories) || otherCategories.length === 0)) {
      return (
        <div className="no-profiles-container">
          <h3 className="no-profiles-title">Currently no profiles in this section</h3>
        </div>
      );
    }
    return (
      <motion.div
        className="other-category-grid"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      >
        {otherCategories.map((category, index) => (
          <motion.div
            key={category.id || index}
            className="other-category-card"
            variants={{
              hidden: { opacity: 0, y: 20, scale: 0.9 },
              visible: { opacity: 1, y: 0, scale: 1 }
            }}
            whileHover={{
              scale: 1.05, y: -5,
              boxShadow: "0 10px 30px rgba(191, 137, 6, 0.2)",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              navigate('/other-section-content', {
                state: { categoryId: category.id, categoryName: category.cat_name }
              });
              handleCloseOtherModal();
            }}
          >
            <div className="other-category-icon-wrapper">
              <img
                src={getCraftImageUrl(category.cat_image)}
                alt={category.cat_name}
                className="other-category-icon"
                onError={(e) => { console.error('Failed to load category image:', category.cat_image); }}
              />
            </div>
            <div className="other-category-info">
              <h3 className="other-category-name">{toTitleCase(category.cat_name)}</h3>
            </div>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Render modal body content for Crafts
  const renderCraftModalContent = () => {
    if (loadingSubCategories) {
      return (
        <div className="modal-loading">
          <motion.div
            className="modal-loader"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p>Loading...</p>
        </div>
      );
    }
    if (subCategoryError) {
      return (
        <div className="modal-error">
          <div className="no-profiles-icon">⚠️</div>
          <h3 className="no-profiles-title">Error Loading Profiles</h3>
          <p className="no-profiles-subtitle">{subCategoryError}</p>
        </div>
      );
    }
    if (!Array.isArray(subCategories) || subCategories.length === 0) {
      return (
        <div className="no-profiles-container">
          <h3 className="no-profiles-title">Currently no profiles in this category</h3>
        </div>
      );
    }
    return (
      <motion.div
        className="modal-subcategory-grid"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      >
        {subCategories.map((subCategory, index) => (
          <motion.div
            key={subCategory.id || index}
            className="subcategory-chip"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            whileHover={{ scale: 1.05, y: -3, transition: { duration: 0.2 } }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              navigate('/subcategory-users', {
                state: {
                  subCategoryId: subCategory.id,
                  subCategoryName: subCategory.sub_cat_name,
                  categoryName: selectedCraft.cat_name,
                  sourcePage: '/'
                }
              });
              handleCloseModal();
            }}
          >
            <span className="subcategory-chip-text">
              {toTitleCase(subCategory.sub_cat_name)}
            </span>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Banner items to render
  const bannerItems = bannerData.length > 0 ? bannerData : fallbackBanners;

  return (
    <div className="page-wrapper">

      {/* ===== HERO VIDEO CAROUSEL ===== */}
      <div className="hero-carousel-section">
        <button className="hero-arrow hero-arrow-left" onClick={heroPrev} aria-label="Previous video">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>

        <div className="hero-carousel-track">
          {/* Left preview */}
          <div className="hero-slide hero-slide-side hero-slide-left" onClick={heroPrev}>
            <iframe
              title="Preview Left"
              src={convertToEmbedUrl(heroVideos[getHeroSlideIndex(-1)])}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="hero-video-iframe"
              tabIndex={-1}
            />
            <div className="hero-slide-overlay" />
          </div>

          {/* Center active */}
          <div className="hero-slide hero-slide-center">
            <iframe
              title="Featured Video"
              src={convertToEmbedUrl(heroVideos[heroIndex], heroPlaying)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="hero-video-iframe"
            />
            {!heroPlaying && (
              <div className="hero-play-overlay" onClick={() => setHeroPlaying(true)}>
                <div className="hero-play-btn">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                </div>
              </div>
            )}
          </div>

          {/* Right preview */}
          <div className="hero-slide hero-slide-side hero-slide-right" onClick={heroNext}>
            <iframe
              title="Preview Right"
              src={convertToEmbedUrl(heroVideos[getHeroSlideIndex(1)])}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="hero-video-iframe"
              tabIndex={-1}
            />
            <div className="hero-slide-overlay" />
          </div>
        </div>

        <button className="hero-arrow hero-arrow-right" onClick={heroNext} aria-label="Next video">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* ===== PROMOTIONAL BANNER STRIP ===== */}
      <div className="promo-banner-section">
        <div className="promo-banner-strip" ref={promoBannerRefTop}>
          {bannerItems.map((item, index) => {
            const imgSrc = typeof item === 'string'
              ? item
              : getBannerImageUrl(item?.banner_img || item?.image || item?.banner_image || item?.url);
            return (
              <div key={index} className="promo-banner-card">
                <img
                  src={imgSrc || fallbackBanners[index % fallbackBanners.length]}
                  alt={item?.title || item?.name || `Banner ${index + 1}`}
                  className="promo-banner-img"
                  onError={(e) => { e.target.src = fallbackBanners[index % fallbackBanners.length]; }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== CATEGORY FILTER PILLS ===== */}
      <div className="category-filter-section">
        {categoryList.map((item, index) => (
          <button
            key={index}
            className={`category-pill ${activeCategory === item.name ? 'category-pill-active' : ''}`}
            onClick={() => {
              setActiveCategory(item.name);
              if (item.name === 'Rentals') {
                // Switch to Other Crafts tab and scroll to Rentals section
                setActiveTab('other');
                if (!loading) {
                  setTimeout(() => {
                    if (rentalsRef.current) {
                      rentalsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 150);
                }
              } else if (item.name === 'Lessons') {
                navigate('/other-section-detail', {
                  state: { sectionId: '181', sectionName: 'Lessons', sectionImage: 'dailyNews.png' }
                });
              } else if (item.path) {
                navigate(item.path);
              }
            }}
          >
            {item.name}
          </button>
        ))}
      </div>

      {/* ===== CONTENT SECTIONS WITH TABS ===== */}
      <div className="content-section-card">
        {/* ===== CRAFT TABS ===== */}
        <div className="craft-tabs-section" style={{ paddingBottom: '20px', borderBottom: '1px solid #eee', marginBottom: '20px' }}>
          <div className="craft-tabs-container">
            <button
              className={`craft-tab-btn ${activeTab === '24crafts' ? 'craft-tab-active' : ''}`}
              onClick={() => setActiveTab('24crafts')}
            >
              24 Crafts
            </button>

            <button
              className={`craft-tab-btn ${activeTab === 'other' ? 'craft-tab-active' : ''}`}
              onClick={() => setActiveTab('other')}
            >
              Other Crafts
            </button>
          </div>
        </div>

        {activeTab === '24crafts' && (
          <div className="content-section-inner">
            <h2 className="content-section-title">24 Crafts</h2>
            <div
              style={{
                borderRadius: '10px',
                background: '#F9F9F9',
                boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                padding: '30px 20px'
              }}
            >
              <div className="content-grid">
                {craftCategories.map((craft, index) => {
                  const craftImgSrc = getCraftImageUrl(craft.cat_image);
                  return (
                    <div
                      key={craft.id || index}
                      className="content-grid-item"
                      onClick={() => handleCraftClick(craft)}
                    >
                      <div className="content-icon-circle">
                        <img
                          src={craftImgSrc}
                          alt={craft.cat_name}
                          className="content-icon-img"
                          onError={(e) => { console.error('Failed to load craft image:', craftImgSrc); }}
                        />
                      </div>
                      <span className="content-item-label">{toTitleCase(craft.cat_name)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'other' && otherSections && (
          <div className="content-section-inner">
            {['Learning', 'Support', 'Information', 'List', 'Video'].map((groupTitle) => {
              const groupItems = otherSections[groupTitle];
              if (!groupItems || groupItems.length === 0) return null;

              return (
                <div key={groupTitle} style={{ marginBottom: '40px' }}>
                  <h2 className="content-section-title" style={{ textAlign: 'center', marginBottom: '15px' }}>{groupTitle}</h2>
                  <div
                    style={{
                      borderRadius: '10px',
                      background: '#F9F9F9',
                      boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                      padding: '30px 20px'
                    }}
                  >
                    <div className="content-grid">
                      {groupItems.map((section, index) => {
                        const sectionImgSrc = getOtherSectionImageUrl(section.sub_picture || section.cat_image);
                        return (
                          <div
                            key={section.id || index}
                            className="content-grid-item"
                            onClick={() => handleOtherSectionClick(section)}
                          >
                            <div className="content-icon-circle">
                              <img
                                src={sectionImgSrc}
                                alt={section.sub_cat_name}
                                className="content-icon-img"
                                onError={(e) => { console.error(`Failed to load section image for ${section.sub_cat_name}:`, sectionImgSrc); }}
                              />
                            </div>
                            <span className="content-item-label">{toTitleCase(section.sub_cat_name)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Rentals group — shows Craft Rentals subcategories directly */}
            {craftRentalsSubcategories.length > 0 && (
              <div ref={rentalsRef} style={{ marginBottom: '40px' }}>
                <h2 className="content-section-title" style={{ textAlign: 'center', marginBottom: '15px' }}>Rentals</h2>
                <div
                  style={{
                    borderRadius: '10px',
                    background: '#F9F9F9',
                    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                    padding: '30px 20px'
                  }}
                >
                  <div className="content-grid">
                    {craftRentalsSubcategories.map((sub, index) => {
                      const subImgSrc = getRentalSubImage(sub.sub_cat_name, sub.sub_picture || sub.cat_image);
                      return (
                        <div
                          key={sub.id || index}
                          className="content-grid-item"
                          onClick={() => navigate('/subcategory-users', {
                            state: {
                              subCategoryId: sub.id,
                              subCategoryName: sub.sub_cat_name,
                              categoryName: 'Rentals',
                              sourcePage: '/'
                            }
                          })}
                        >
                          <div className="content-icon-circle">
                            <img
                              src={subImgSrc}
                              alt={sub.sub_cat_name}
                              className="content-icon-img"
                              onError={(e) => { console.error('Failed to load rental sub image:', subImgSrc); }}
                            />
                          </div>
                          <span className="content-item-label">{toTitleCase(sub.sub_cat_name)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== BOTTOM PROMOTIONAL BANNERS ===== */}
      <div className="promo-banner-section promo-banner-bottom">
        <div className="promo-banner-strip" ref={promoBannerRefBottom}>
          {(bannerData.length > 0 ? bannerData : fallbackBanners).map((item, index) => {
            const imgSrc = typeof item === 'string'
              ? item
              : getBannerImageUrl(item?.banner_img || item?.image || item?.banner_image || item?.url);
            return (
              <div key={`bottom-${index}`} className="promo-banner-card">
                <img
                  src={imgSrc || fallbackBanners[index % fallbackBanners.length]}
                  alt={item?.title || item?.name || `Banner ${index + 1}`}
                  className="promo-banner-img"
                  onError={(e) => { e.target.src = fallbackBanners[index % fallbackBanners.length]; }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal for 24 Crafts */}
      <AnimatePresence>
        {isModalOpen && selectedCraft && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.8, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.8, y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative Background Elements */}
              <div className="modal-bg-decoration">
                <div className="modal-circle modal-circle-1"></div>
                <div className="modal-circle modal-circle-2"></div>
                <div className="modal-circle modal-circle-3"></div>
              </div>

              {/* Modal Header */}
              <div className="modal-header">
                <motion.div
                  className="modal-header-left"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="modal-logo-container">
                    <img
                      src={getCraftImageUrl(selectedCraft.cat_image)}
                      alt={selectedCraft.cat_name}
                      className="modal-craft-logo"
                    />
                  </div>
                  <h2 className="modal-craft-title">{toTitleCase(selectedCraft.cat_name)}</h2>
                </motion.div>
                <motion.button
                  className="modal-close-btn"
                  onClick={handleCloseModal}
                  whileHover={{ rotate: 90, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ✕
                </motion.button>
              </div>

              {/* Modal Body */}
              <div className="modal-body">
                {renderCraftModalContent()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal for Other Sections */}
      <AnimatePresence>
        {isOtherModalOpen && selectedOtherSection && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleCloseOtherModal}
          >
            <motion.div
              className="modal-content other-section-modal"
              initial={{ scale: 0.8, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.8, y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative Background Elements */}
              <div className="modal-bg-decoration other-modal-bg">
                <div className="modal-circle modal-circle-1"></div>
                <div className="modal-circle modal-circle-2"></div>
                <div className="modal-circle modal-circle-3"></div>
              </div>

              {/* Modal Header */}
              <div className="modal-header other-modal-header">
                <motion.div
                  className="modal-header-left"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="modal-logo-container">
                    <img
                      src={getOtherSectionImageUrl(selectedOtherSection.sub_picture || selectedOtherSection.cat_image)}
                      alt={selectedOtherSection.sub_cat_name}
                      className="modal-craft-logo"
                    />
                  </div>
                  <div className="modal-header-info">
                    <h2 className="modal-craft-title">{toTitleCase(selectedOtherSection.sub_cat_name)}</h2>
                  </div>
                </motion.div>
                <motion.button
                  className="modal-close-btn"
                  onClick={handleCloseOtherModal}
                  whileHover={{ rotate: 90, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ✕
                </motion.button>
              </div>

              {/* Modal Body */}
              <div className="modal-body">
                {renderOtherModalContent()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default HomePage;